/**
 * Obstruction Map Colors (WebGL-safe hex values)
 *
 * MapLibre paint expressions cannot resolve CSS custom properties at runtime,
 * so the obstruction symbol layer needs concrete hex values for severity halo
 * and status fallback. These hex values mirror the same Mantine palette
 * tokens used by the obstruction badge/chip UI (`src/constants/obstructions.ts`):
 *   - SeverityLevel → Mantine gray/yellow/orange/red `*-6`
 *   - ObstructionStatus → shared `--app-status-*` palette (light-mode values)
 *
 * Rule F-N3 whitelists hex literals under `src/constants/` — this is where
 * they belong, not in component files.
 *
 * @module constants/obstructionMapColors
 */

import {
  ObstructionStatus,
  ObstructionType,
  SeverityLevel,
} from '@/types/obstructions';

// ===== SEVERITY HEX COLORS (WebGL) =====

/**
 * Hex equivalents of `SEVERITY_COLORS` (Mantine `gray-6`, `yellow-6`,
 * `orange-6`, `red-6` from `main.tsx`). Used by the obstruction symbol
 * layer's severity-halo paint expression.
 */
export const OBSTRUCTION_SEVERITY_HEX_COLORS: Record<SeverityLevel, string> = {
  [SeverityLevel.Low]: '#868e96', // mantine gray-6
  [SeverityLevel.Medium]: '#fab005', // mantine yellow-6
  [SeverityLevel.High]: '#fd7e14', // mantine orange-6
  [SeverityLevel.Critical]: '#fa5252', // mantine red-6
} as const;

// ===== STATUS HEX COLORS (WebGL) =====

/**
 * Hex equivalents of `OBSTRUCTION_STATUS_COLORS` — light-mode values from the
 * `--app-status-*` palette used by task markers as well. Drives the inner
 * disc color of the obstruction anchor symbol.
 */
export const OBSTRUCTION_STATUS_HEX_COLORS: Record<ObstructionStatus, string> =
  {
    [ObstructionStatus.Planned]: '#868e96',
    [ObstructionStatus.Scheduled]: '#868e96',
    [ObstructionStatus.Active]: '#4c6ef5',
    [ObstructionStatus.Extended]: '#fab005',
    [ObstructionStatus.Completed]: '#40c057',
    [ObstructionStatus.Archived]: '#adb5bd',
  } as const;

// ===== OBSTRUCTION TYPE SPRITE KEY MAP =====

/**
 * Sprite-atlas key per `ObstructionType`. Used by the symbol layer's `match`
 * expression on `['get', 'type']`. Keys mirror the type enum value so the
 * MapLibre expression stays a 1:1 mapping.
 */
export const OBSTRUCTION_TYPE_SPRITE_KEYS: Record<ObstructionType, string> = {
  [ObstructionType.Maintenance]: 'obstruction-Maintenance',
  [ObstructionType.Construction]: 'obstruction-Construction',
  [ObstructionType.Accident]: 'obstruction-Accident',
  [ObstructionType.Closure]: 'obstruction-Closure',
  [ObstructionType.Event]: 'obstruction-Event',
  [ObstructionType.Emergency]: 'obstruction-Emergency',
} as const;
